import React, { useState } from "react";
import { Dialog, Drawer, FormControlLabel, Checkbox, TextField, InputAdornment } from "@material-ui/core";
import InfiniteScroll from "react-infinite-scroller";
import { P_Props } from "./CareerListing.type";
import { CustomButton, CustomStatus, FloatingButton, Loading, Notification, Pulse } from "../../components";
import { COUNTRY, TEXT } from "../../../constants";
import { DeleteIcon, EditIcon, FilterIcon } from "../../icons";
import { CareerStatus } from "../../../models";
import { CareerForm } from "..";
import moment from "moment";
import { PriorityHigh as PriorityHighIcon, Close as CloseIcon, Search as SearchIcon } from "@material-ui/icons";
import { currencyUnitFormat } from "../../../utils";

const CareerListingPresenter: React.FC<P_Props> = (props) => {
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<{
    visible: boolean;
    code: string | null;
    title: string | null;
  }>({ visible: false, code: null, title: null });
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const toggleDeleteModal = (code?: string, title?: string) => {
    code && title
      ? setVisibleDeleteModal({ visible: true, code, title })
      : setVisibleDeleteModal({ visible: false, code: null, title: null });
  };

  const openFilterForm = () => {
    setOpenFilter(true);
  };

  const closeFilterForm = () => {
    setOpenFilter(false);
  };

  return (
    <>
      <div className="career-listing">
        <div className="career-listing__content">
          <div className="career-listing__content__header">
            <div className="career-listing__content__header__title">Careers</div>
            <div className="career-listing__content__header__actions">
              <div className="career-listing__content__header__search">
                <TextField
                  placeholder="Search by name, ID"
                  variant="outlined"
                  className="search-input"
                  inputProps={{ className: "input" }}
                  InputProps={{
                    // style: { fontSize: "1.4rem" },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="icon" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    props.onSearch(e.target.value);
                  }}
                />
              </div>
              <div className="career-listing__content__header__filter" onClick={openFilterForm}>
                <FilterIcon size={[18, 18]} viewBox={[18, 18]} color={"#272B2F"} />
                <h4 className="career-listing__content__header__filter__title">Filter</h4>
              </div>
            </div>
          </div>
          <div className="career-listing__content__list">
            <div className="career-listing__content__list__header">
              <div className="text career-listing__content__list__title">Title</div>
              <div className="text career-listing__content__list__country">Country</div>
              <div className="text career-listing__content__list__salary">Salary range</div>
              <div className="text career-listing__content__list__status">Status</div>
              <div className="text career-listing__content__list__date">Closing Date</div>
              <div className="text career-listing__content__list__action"></div>
            </div>
            <InfiniteScroll
              useWindow={false}
              pageStart={1}
              initialLoad={false}
              hasMore={!props.fetchedCareer.loading && props.fetchedCareer.totalPages > props.fetchedCareer.page}
              loadMore={() => {
                props.onLoadMore();
              }}
            >
              {!Boolean(props.fetchedCareer.data.length) && props.fetchedCareer.loading && <Loading />}
              {props.fetchedCareer.data.map((cr) => {
                return (
                  <div key={cr.id} className="career-listing__content__list__item">
                    <div
                      className="career-text career-listing__content__list__title"
                      onClick={() => {
                        props.openCareerForm(cr.code);
                      }}
                    >
                      {cr.title}
                    </div>
                    <div className="career-listing__content__list__country">
                      <div>
                        <div className="career-text info__name">
                          {COUNTRY.find((e) => e.countryName === cr.country)?.countryName}
                        </div>
                      </div>
                    </div>
                    <div className="career-text career-listing__content__list__salary">
                      {currencyUnitFormat(cr.minSalary ?? 0, cr.currency)} -{" "}
                      {currencyUnitFormat(cr.maxSalary ?? 0, cr.currency)}
                    </div>
                    <div className="career-text career-listing__content__list__status">
                      <CustomStatus
                        color={cr.status === CareerStatus.PUBLISH ? "#08C4E3" : "#BDC6D7"}
                        title={cr.status === CareerStatus.PUBLISH ? CareerStatus.PUBLISH : CareerStatus.UNPUBLISH}
                        style={{ textTransform: "capitalize" }}
                      />
                    </div>
                    <div className="career-text career-listing__content__list__salary">
                      {cr.closingDate ? moment(cr.closingDate).format("DD/MM/yyyy") : TEXT.EMPTY}
                    </div>
                    <div className="career-text career-listing__content__list__action">
                      <CustomButton
                        onClick={(e) => {
                          e.stopPropagation();
                          props.openCareerForm(cr.code);
                        }}
                        color={"transparent"}
                        item={
                          <span>
                            <EditIcon size={[20, 20]} viewBox={[20, 20]} color={"#272B2F"} />
                          </span>
                        }
                      />
                      <CustomButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDeleteModal(cr.code, cr.title);
                        }}
                        color={"transparent"}
                        item={
                          <span>
                            <DeleteIcon size={[20, 20]} viewBox={[20, 20]} color={"#272B2F"} />
                          </span>
                        }
                      />
                    </div>
                  </div>
                );
              })}
              {Boolean(props.fetchedCareer.data.length) && props.fetchedCareer.loading && <Loading />}
            </InfiniteScroll>
          </div>
        </div>
      </div>
      <FloatingButton visible={false} onClickPrimary={() => props.openCareerForm()} btns={[]} />
      <Dialog fullScreen open={props.visibleCareerForm}>
        <CareerForm
          careerCode={props.visibleCareerId}
          onCancel={() => {
            props.closeCareerForm();
          }}
          onComplete_Create={() => {
            props.closeCareerForm();
            props.onRefreshCareerList();
          }}
          onComplete_Update={() => {
            props.closeCareerForm();
            props.onRefreshCareerList();
          }}
          onComplete_Delete={() => {
            props.closeCareerForm();
            props.onRefreshCareerList();
          }}
        />
      </Dialog>

      <Notification
        visible={visibleDeleteModal.visible}
        title="Delete Career"
        subTitle={`Are you sure you want to delete “${visibleDeleteModal.title}“ career? This action cannot be undone`}
        icon={
          <Pulse color={"red"}>
            <div className="notification-icon-box red">
              <PriorityHighIcon style={{ fontSize: "5rem", color: "#fff" }} />
            </div>
          </Pulse>
        }
        actions={
          <>
            <button
              className="blog-form__delete-modal__actions__cancel"
              onClick={() => {
                toggleDeleteModal();
              }}
            >
              Cancel
            </button>
            <button
              className="blog-form__delete-modal__actions__confirm"
              onClick={() => {
                toggleDeleteModal();
                props.onDeleteCareer(visibleDeleteModal.code);
              }}
            >
              Confirm
            </button>
          </>
        }
      />

      <Drawer anchor="right" open={openFilter} onClose={closeFilterForm}>
        <div className="drawer">
          <div className="drawer-header">
            <div className="drawer-header--title">Filter</div>
            <div className="drawer-header--close">
              <button
                onClick={closeFilterForm}
                className="simple-modal-header__close"
                style={{ marginRight: "2.6rem" }}
              >
                <CloseIcon style={{ fontSize: "2.2rem" }} />
              </button>
            </div>
          </div>
          <div className="drawer-body--field">
            <div className="drawer-body--field__title">Status</div>
            <FormControlLabel
              control={
                <>
                  <Checkbox
                    checked={props.statusFilter.publish}
                    onChange={(e) => props.handleChangeCheckBox(e)}
                    name="publish"
                  />
                  <div className="custom-status__icon" style={{ backgroundColor: "#08c4e3" }} />
                </>
              }
              label="Publish"
            />
            <FormControlLabel
              control={
                <>
                  <Checkbox
                    checked={props.statusFilter.unpublish}
                    onChange={(e) => props.handleChangeCheckBox(e)}
                    name="unpublish"
                  />
                  <div className="custom-status__icon" style={{ backgroundColor: "#BDC6D7" }} />
                </>
              }
              label="Unpublish"
            />
          </div>
          <div className="drawer-footer">
            <button type="reset" className="btn cancel" onClick={props.clearFilter}>
              Clear filter
            </button>
            <button
              className="btn save"
              onClick={() => {
                closeFilterForm();
                props.onApplyStatusFilter();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CareerListingPresenter;
